import { apiRequest, refreshSession } from '../api/client';
import { useSessionStore } from '../auth/session-store';

export const ORDER_STATUSES = ['DRAFT', 'SUBMITTED', 'CONFIRMED', 'CANCELLED'] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const ORDER_SOURCES = ['BACKOFFICE', 'CLIENT_PORTAL', 'TEMPLATE'] as const;
export type OrderSource = (typeof ORDER_SOURCES)[number];

export const ORDER_SOURCE_LABELS: Record<OrderSource, string> = {
  BACKOFFICE: 'Back-office',
  CLIENT_PORTAL: 'Portal cliente',
  TEMPLATE: 'Plantilla',
};

/** Matches `apps/api` `OrderItemPositionSummary` (`order.repository.ts`), frozen at resolution time. */
export interface OrderItemPositionSummary {
  id: string;
  code: string;
  name: string;
  displayOrder: number;
}

/**
 * Matches `apps/api` `StoredOrderItem` (`order.repository.ts`). Money
 * fields are cents, already `number` (never `bigint`) on the wire.
 * `positionId`/`position` are `null` only for items frozen before
 * `rotating-menu` (`design.md` §1.4, T6): orders migrated from a
 * calendar entry that no longer exists.
 */
export const PRICE_SOURCES = ['CONTRACT_MENU', 'CONTRACT_SERVICE_TYPE', 'CATALOG'] as const;
export type PriceSource = (typeof PRICE_SOURCES)[number];

export interface OrderItem {
  id: string;
  menuId: string;
  menuNameSnapshot: string;
  positionId: string | null;
  position: OrderItemPositionSummary | null;
  quantity: number;
  unitPriceCents: number;
  vatRate: number;
  /** `null` on lines created before the origin was recorded. */
  priceSource: PriceSource | null;
  subtotalCents: number;
  notes: string | null;
}

/** Matches `apps/api` `StoredOrder` (`order.repository.ts`). */
export interface Order {
  id: string;
  orderNumber: string;
  customerId: string;
  customerName: string;
  siteId: string;
  siteName: string;
  contractId: string;
  /** Copied from the contract when the order is created; absent on orders that predate points of sale. */
  operatingUnitId?: string | null;
  operatingUnitName?: string | null;
  serviceDate: string;
  status: OrderStatus;
  source: OrderSource;
  templateId: string | null;
  totalQuantity: number;
  subtotalCents: number;
  vatCents: number;
  totalCents: number;
  cutoffAt: string;
  cutoffOverridden: boolean;
  cutoffOverrideReason: string | null;
  confirmedAt: string | null;
  confirmedBy: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  clientNotes: string | null;
  internalNotes: string | null;
  items: OrderItem[];
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
}

/** `rotating-menu/design.md` §3.4: one line per position, `positionId` — the server resolves the dish published in that position that day. */
export interface OrderLineInput {
  positionId: string;
  quantity: number;
  notes?: string | null;
}

/** Matches `apps/api` `createOrderSchema` (`order.dto.ts`). */
export interface OrderCreateInput {
  customerId: string;
  siteId: string;
  /** Required by the API only when the customer has several active contracts (`OPERATING_UNIT_REQUIRED`). */
  operatingUnitId?: string;
  serviceDate: string;
  items: OrderLineInput[];
  clientNotes?: string | null;
  internalNotes?: string | null;
  cutoffOverrideReason?: string;
}

/** Matches `apps/api` `updateOrderSchema`. */
export interface OrderUpdateInput {
  items: OrderLineInput[];
  clientNotes?: string | null;
  internalNotes?: string | null;
  cutoffOverrideReason?: string;
}

/** Matches `apps/api` `POST /orders`. */
export function createOrder(input: OrderCreateInput): Promise<Order> {
  return apiRequest<Order>('/orders', { method: 'POST', body: JSON.stringify(input) });
}

/** Matches `apps/api` `PATCH /orders/:id`. */
export function updateOrder(id: string, input: OrderUpdateInput): Promise<Order> {
  return apiRequest<Order>(`/orders/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

/** Matches `apps/api` `GET /orders/:id`. */
export function getOrder(id: string): Promise<Order> {
  return apiRequest<Order>(`/orders/${id}`);
}

/** Matches `apps/api` `POST /orders/:id/submit`. */
export function submitOrder(id: string): Promise<Order> {
  return apiRequest<Order>(`/orders/${id}/submit`, { method: 'POST' });
}

/** Matches `apps/api` `POST /orders/:id/cancel`. */
export function cancelOrder(id: string, reason: string): Promise<Order> {
  return apiRequest<Order>(`/orders/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export interface OrderSearchParams {
  serviceDate?: string | undefined;
  customerId?: string | undefined;
  siteId?: string | undefined;
  operatingUnitId?: string | undefined;
  status?: OrderStatus | undefined;
  source?: OrderSource | undefined;
  page: number;
  pageSize: number;
}

export interface OrderSearchResult {
  items: Order[];
  page: number;
  pageSize: number;
  total: number;
}

/** The largest page `GET /orders` accepts; anything above is a 400 (`order.dto.ts`'s `listOrdersQuerySchema`). Unlike `/calendar`, this listing grows every day, so it really does paginate. */
export const MAX_ORDER_PAGE_SIZE = 100;

/** Matches `apps/api` `GET /orders` (`listOrdersQuerySchema`). */
export function listOrders(params: OrderSearchParams): Promise<OrderSearchResult> {
  const query = new URLSearchParams({
    page: String(params.page),
    pageSize: String(Math.min(params.pageSize, MAX_ORDER_PAGE_SIZE)),
  });

  if (params.serviceDate !== undefined) {
    query.set('serviceDate', params.serviceDate);
  }
  if (params.customerId !== undefined) {
    query.set('customerId', params.customerId);
  }
  if (params.siteId !== undefined) {
    query.set('siteId', params.siteId);
  }
  if (params.operatingUnitId !== undefined) {
    query.set('operatingUnitId', params.operatingUnitId);
  }
  if (params.status !== undefined) {
    query.set('status', params.status);
  }
  if (params.source !== undefined) {
    query.set('source', params.source);
  }

  return apiRequest<OrderSearchResult>(`/orders?${query.toString()}`);
}

export interface DeviationResult {
  averageQuantity: number | null;
  deviationRatio: number | null;
  isDeviating: boolean;
}

/** Matches `apps/api` `GET /orders/:id/deviation`. */
export function getOrderDeviation(id: string): Promise<DeviationResult> {
  return apiRequest<DeviationResult>(`/orders/${id}/deviation`);
}

/**
 * `GET /orders/export` answers CSV, not JSON (`ExportOrdersUseCase`'s own
 * documented deviation: no `xlsx` dependency in `apps/api`) — this cannot
 * go through `apiRequest`, which always parses the body as JSON. Triggers
 * a browser download directly instead of returning the text to the
 * caller, the same UX "Exportar CSV" implies regardless of the
 * underlying format.
 */
export async function exportOrders(
  params: Omit<OrderSearchParams, 'page' | 'pageSize'>,
): Promise<void> {
  const query = new URLSearchParams();

  if (params.serviceDate !== undefined) {
    query.set('serviceDate', params.serviceDate);
  }
  if (params.customerId !== undefined) {
    query.set('customerId', params.customerId);
  }
  if (params.siteId !== undefined) {
    query.set('siteId', params.siteId);
  }
  if (params.operatingUnitId !== undefined) {
    query.set('operatingUnitId', params.operatingUnitId);
  }
  if (params.status !== undefined) {
    query.set('status', params.status);
  }
  if (params.source !== undefined) {
    query.set('source', params.source);
  }

  function sendExportRequest(): Promise<Response> {
    const token = useSessionStore.getState().session?.accessToken;

    return fetch(`/api/v1/orders/export?${query.toString()}`, {
      headers: token === undefined ? {} : { authorization: `Bearer ${token}` },
      credentials: 'include',
    });
  }

  let response = await sendExportRequest();

  // FINDINGS.md → segunda corrida: reproduced live 3/3 — the export used a
  // bare `fetch()` with no refresh-and-retry, unlike every call routed
  // through `apiRequest`. A concurrently rotated access token landed here
  // as a plain, unrecovered 401 and the download silently never started.
  if (response.status === 401) {
    const renewed = await refreshSession();

    if (renewed) {
      response = await sendExportRequest();
    }
  }

  if (!response.ok) {
    throw new Error('No pudimos exportar el listado.');
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = 'orders.csv';
  link.click();

  // `link.click()` only *starts* the browser's download; revoking the blob
  // URL in the same tick raced it under load and intermittently invalidated
  // the URL before the browser had read it (FINDINGS.md → segunda corrida,
  // "Exportar CSV"). Deferring the revoke lets the download begin first.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export interface DayStatusEntry {
  customerId: string;
  customerName: string;
  siteId: string | null;
  siteName: string | null;
  operatingUnitId: string | null;
  operatingUnitName: string | null;
  hasOrder: boolean;
  orderId: string | null;
  status: OrderStatus | null;
  totalQuantity: number;
  isBelowMinimum: boolean;
  deviation: DeviationResult | null;
  /** The order was confirmed/cancelled by the scheduled close, not by a user. */
  closedAutomatically: boolean;
}

/** Matches `apps/api` `DayStatusSummary` (`get-day-status.use-case.ts`). */
export interface DayStatusSummary {
  serviceDate: string;
  cutoffStatus: 'OPEN' | 'CLOSED';
  entries: DayStatusEntry[];
}

/** Matches `apps/api` `GET /orders/day-status`. */
export function getDayStatus(
  serviceDate: string,
  operatingUnitId?: string,
): Promise<DayStatusSummary> {
  const query = new URLSearchParams({ serviceDate });

  if (operatingUnitId !== undefined) {
    query.set('operatingUnitId', operatingUnitId);
  }

  return apiRequest<DayStatusSummary>(`/orders/day-status?${query.toString()}`);
}

export interface RemindMissingOrdersResult {
  remindedCount: number;
}

/** Matches `apps/api` `POST /orders/day-status/remind`. */
export function remindMissingOrders(serviceDate: string): Promise<RemindMissingOrdersResult> {
  return apiRequest<RemindMissingOrdersResult>('/orders/day-status/remind', {
    method: 'POST',
    body: JSON.stringify({ serviceDate }),
  });
}

export interface CloseCutoffResult {
  confirmedCount: number;
  cancelledCount: number;
  belowMinimumOrderIds: string[];
}

/**
 * Matches `apps/api` `POST /orders/close-cutoff`. The cutoff closes per point of sale
 * (`order-cutoff-close/spec.md`): `operatingUnitId` limits it to one.
 */
export function closeCutoff(
  serviceDate: string,
  operatingUnitId?: string,
): Promise<CloseCutoffResult> {
  return apiRequest<CloseCutoffResult>('/orders/close-cutoff', {
    method: 'POST',
    body: JSON.stringify(
      operatingUnitId === undefined ? { serviceDate } : { serviceDate, operatingUnitId },
    ),
  });
}

/** ISO weekday, 1 = lunes … 7 = domingo, same numbering `createOrderTemplateSchema` (`apps/api`) accepts — same convention as `contracts/api.ts`'s own copy, this domain keeps its own. */
export const DAYS_OF_WEEK = [1, 2, 3, 4, 5, 6, 7] as const;

export const DAY_OF_WEEK_LABELS: Record<(typeof DAYS_OF_WEEK)[number], string> = {
  1: 'Lunes',
  2: 'Martes',
  3: 'Miércoles',
  4: 'Jueves',
  5: 'Viernes',
  6: 'Sábado',
  7: 'Domingo',
};

export interface OrderTemplateItem {
  id: string;
  positionId: string;
  quantity: number;
  notes: string | null;
}

/** Matches `apps/api` `StoredOrderTemplate` (`order-template.repository.ts`). */
export interface OrderTemplate {
  id: string;
  customerId: string;
  siteId: string;
  operatingUnitId: string;
  name: string;
  daysOfWeek: number[];
  isActive: boolean;
  items: OrderTemplateItem[];
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
}

export interface OrderTemplateItemInput {
  positionId: string;
  quantity: number;
  notes: string | null;
}

/** Matches `apps/api` `createOrderTemplateSchema`. */
export interface OrderTemplateCreateInput {
  customerId: string;
  siteId: string;
  /** Required by the API only when the customer has several active contracts (`OPERATING_UNIT_REQUIRED`). */
  operatingUnitId?: string;
  name: string;
  daysOfWeek: number[];
  items: OrderTemplateItemInput[];
}

/** Matches `apps/api` `updateOrderTemplateSchema`. */
export interface OrderTemplateUpdateInput {
  name?: string;
  daysOfWeek?: number[];
  isActive?: boolean;
  items?: OrderTemplateItemInput[];
}

/** Matches `apps/api` `GET /orders/templates` — no pagination (`listOrderTemplatesQuerySchema` has no `page`/`pageSize`, this list stays small: one row per recurring client+site combination). */
export function listOrderTemplates(): Promise<OrderTemplate[]> {
  return apiRequest<OrderTemplate[]>('/orders/templates');
}

/** Matches `apps/api` `POST /orders/templates`. */
export function createOrderTemplate(input: OrderTemplateCreateInput): Promise<OrderTemplate> {
  return apiRequest<OrderTemplate>('/orders/templates', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Matches `apps/api` `PATCH /orders/templates/:id`. */
export function updateOrderTemplate(
  id: string,
  input: OrderTemplateUpdateInput,
): Promise<OrderTemplate> {
  return apiRequest<OrderTemplate>(`/orders/templates/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/** Matches `apps/api` `DELETE /orders/templates/:id` (soft delete/deactivate). */
export function deleteOrderTemplate(id: string): Promise<void> {
  return apiRequest<void>(`/orders/templates/${id}`, { method: 'DELETE' });
}

export interface GenerateOrdersResult {
  created: string[];
  skipped: { templateId: string; reason: string }[];
}

/** Matches `apps/api` `POST /orders/templates/generate`. */
export function generateOrdersFromTemplates(serviceDate: string): Promise<GenerateOrdersResult> {
  return apiRequest<GenerateOrdersResult>('/orders/templates/generate', {
    method: 'POST',
    body: JSON.stringify({ serviceDate }),
  });
}

export const CHANGE_REQUEST_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'APPLIED'] as const;
export type ChangeRequestStatus = (typeof CHANGE_REQUEST_STATUSES)[number];

/** `rotating-menu/design.md` §3.4: a change request line names a position, whose dish is only resolved at approval time. */
export interface RequestedChangeLine {
  positionId: string;
  quantity: number;
  notes?: string | null;
}

/** Matches `apps/api` `StoredChangeRequest` (`change-request.repository.ts`). */
export interface OrderChangeRequest {
  id: string;
  orderId: string;
  requestedBy: string;
  status: ChangeRequestStatus;
  requestedChanges: { items: RequestedChangeLine[] };
  rejectionReason: string | null;
  approvedBy: string | null;
  appliedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** One line whose requested quantity actually differs from what the order currently has — unchanged lines are omitted server-side (`apps/api` `ChangeRequestPrismaRepository.list()`). Keyed by `positionId` (`rotating-menu/design.md` §3.4), not `menuId`. */
export interface ChangeRequestLineChange {
  positionId: string;
  positionName: string;
  oldQuantity: number;
  newQuantity: number;
}

/**
 * Matches `apps/api` `ChangeRequestListItem` (`change-request.repository.ts`)
 * — only `GET /orders/change-requests` returns this enriched shape; the
 * mutation endpoints (create/approve/reject) still answer the bare
 * `OrderChangeRequest`, so this stays a superset rather than replacing it.
 */
export interface ChangeRequestListItem extends OrderChangeRequest {
  orderNumber: string;
  requesterFullName: string | null;
  lineChanges: ChangeRequestLineChange[];
}

/** Matches `apps/api` `POST /orders/:id/change-requests`. */
export function createChangeRequest(
  orderId: string,
  items: RequestedChangeLine[],
): Promise<OrderChangeRequest> {
  return apiRequest<OrderChangeRequest>(`/orders/${orderId}/change-requests`, {
    method: 'POST',
    body: JSON.stringify({ items }),
  });
}

export interface ChangeRequestSearchParams {
  status?: ChangeRequestStatus | undefined;
  page: number;
  pageSize: number;
}

export interface ChangeRequestSearchResult {
  items: ChangeRequestListItem[];
  page: number;
  pageSize: number;
  total: number;
}

/** No `orderId` filter on `GET /orders/change-requests` (`listChangeRequestsQuerySchema`): `OrderDetailPage`'s Cambios tab filters this same response client-side by `orderId`, same criterion `CalendarListPage` already applies to `serviceTypeId`. */
export const MAX_CHANGE_REQUEST_PAGE_SIZE = 100;

/** Matches `apps/api` `GET /orders/change-requests` (`listChangeRequestsQuerySchema`). */
export function listChangeRequests(
  params: ChangeRequestSearchParams,
): Promise<ChangeRequestSearchResult> {
  const query = new URLSearchParams({
    page: String(params.page),
    pageSize: String(Math.min(params.pageSize, MAX_CHANGE_REQUEST_PAGE_SIZE)),
  });

  if (params.status !== undefined) {
    query.set('status', params.status);
  }

  return apiRequest<ChangeRequestSearchResult>(`/orders/change-requests?${query.toString()}`);
}

/** Matches `apps/api` `POST /orders/change-requests/:id/approve`. */
export function approveChangeRequest(id: string): Promise<OrderChangeRequest> {
  return apiRequest<OrderChangeRequest>(`/orders/change-requests/${id}/approve`, {
    method: 'POST',
  });
}

/** Matches `apps/api` `POST /orders/change-requests/:id/reject`. */
export function rejectChangeRequest(
  id: string,
  rejectionReason: string,
): Promise<OrderChangeRequest> {
  return apiRequest<OrderChangeRequest>(`/orders/change-requests/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ rejectionReason }),
  });
}

export interface OrderHistoryEntry {
  id: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'STATUS_CHANGE' | 'LOGIN' | 'EXPORT' | 'IMPERSONATE';
  changedFields: string[];
  actorFullName: string | null;
  createdAt: string;
}

/** Matches `apps/api` `GET /orders/:id/history`. Newest first, same permission as `getOrder` (`order.read`). */
export function getOrderHistory(orderId: string): Promise<OrderHistoryEntry[]> {
  return apiRequest<OrderHistoryEntry[]>(`/orders/${orderId}/history`);
}
